import React, { useEffect, useState } from "react";
import SideBar from "../components/SideBar";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../constant";

function ManageProduct() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [category, setCategory] = useState("all");
  const [subcategory, setSubcategory] = useState("all");
  const itemsPerPage = 6;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([{ id: "all", name: "All" }]);
  const [subcategories, setSubcategories] = useState([{ id: "all", name: "All" }]);

  // Create axios instance with auth headers
  const api = axios.create({
    baseURL: API_URL 
  });

  // Add token to requests
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers.Accept = "application/json";
    }
    return config;
  });

  // Fetch products
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await api.get("/superadmin/products");
      
      if (response.data && response.data.products) {
        setProducts(response.data.products);
        
        // Extract unique categories and subcategories
        const uniqueCategories = new Map();
        const uniqueSubcategories = new Map();
        
        response.data.products.forEach((product) => {
          if (product.category && product.category.id) {
            uniqueCategories.set(product.category.id, product.category);
          }
          if (product.subcategory && product.subcategory.id) {
            uniqueSubcategories.set(product.subcategory.id, product.subcategory);
          }
        });
        
        setCategories([
          { id: "all", name: "All" },
          ...Array.from(uniqueCategories.values())
        ]);
        
        setSubcategories([
          { id: "all", name: "All" },
          ...Array.from(uniqueSubcategories.values())
        ]);
      }
    } catch (error) {
      console.error(
        "Failed to fetch products:",
        error.response?.data || error.message,
      );
      // Handle unauthorized access
      if (error.response?.status === 401) {
        localStorage.removeItem("auth_token");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const goToCreateAccount = () => {
    navigate("/create-product");
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search, category, subcategory]);

  const filteredProducts = products.filter((product) => {
    const searchMatch = product.name?.toLowerCase().includes(search.toLowerCase()) ||
      product.slug?.toLowerCase().includes(search.toLowerCase());

    const categoryMatch = category === "all" || 
      (product.category && product.category.id === Number(category));
    
    const subCategoryMatch = subcategory === "all" || 
      (product.subcategory && product.subcategory.id === Number(subcategory));

    return searchMatch && categoryMatch && subCategoryMatch;
  });

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProduct = filteredProducts.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handleRowClick = (product) => {
    navigate("/product-details", { state: { product } });
  };

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{ border: 0, boxShadow: "none" }}>
          <div className="header-top">
            <h2 className="header-title">Manage Product</h2>
            <button className="action-btn" onClick={goToCreateAccount}>
              + Add New Product
            </button>
          </div>

          <div className="filters">
            <div className="input-group">
              <label>Filter by category:</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="filter-select"
              >
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label>Filter by sub category:</label>
              <select
                value={subcategory}
                onChange={(e) => setSubcategory(e.target.value)}
                className="filter-select"
              >
                {subcategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by name or slug"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
               maxLength={20} />
            </div>
          </div>

          <div className="table-container">
            <table className="member-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Product Name</th>
                  <th>Slug</th>
                  <th>Category</th>
                  <th>Sub Category</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center" }}>
                      Loading...
                    </td>
                  </tr>
                ) : paginatedProduct.length > 0 ? (
                  paginatedProduct.map((product, i) => (
                    <tr 
                      key={product.id || i} 
                      onClick={() => handleRowClick(product)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{product.id}</td>
                      <td>{product.name || "N/A"}</td>
                      <td>{product.slug || "N/A"}</td>
                      <td>{product.category?.name || "No category"}</td>
                      <td>{product.subcategory?.name || "No subcategory"}</td>
                      <td>
                        <span className={`status ${product.status?.toLowerCase()}`}>
                          {product.status || "N/A"}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center" }}>
                      No products found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {!loading && filteredProducts.length > 0 && (
              <div className="pagination">
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className="page-btn"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManageProduct;